import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core"
import {
  Assignment,
  CheckCircleOutline,
  Description,
  Schedule,
  TrackChanges,
  Visibility
} from "@material-ui/icons"
import {useEffect, useMemo, useState} from "react"
import AssessmentQuestionWizard from "../../../../components/quiz-assessment/assessment-question-wizard"
import {useQuizAssessmentTestDataProvider} from "../../../../providers/data-provider/quiz-assessment-test.data.provider"
import {QuizAssessmentTestGradeComponent} from "../../../../resources/quiz-assessment-availability/component/quiz-assessment-test-grade.component"

import {SectionHeader} from "./section-header"
import {StatCard} from "./stat-card"
import {StatusChip, StatusPrintedChip} from "./status-chip"
import {useStyles} from "./styles"
import {formatDateTime} from "./utils"

const assessmentTypeLabels = {
  online: 'Online',
  printed: 'Impressa',
}

export function LastTests() {
  const classes = useStyles()
  const [lastTestes, setLastTestes] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [selectedTest, setSelectedTest] = useState(null)
  const {listStudentResults} = useQuizAssessmentTestDataProvider()

  const handleChangePage = (_, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    listStudentResults().then(res => setLastTestes(res.data))
  }, [listStudentResults])

  const stats = useMemo(() => {
    const finalized = lastTestes.filter(t => t.status === 'finished').length
    const notFinalized = lastTestes.filter(t => t.status !== 'finished').length
    const finishedWithData = lastTestes.filter(
      t => t.status === 'finished' && t.totalQuestions > 0
    )
    const avgAccuracy = finishedWithData.length > 0
      ? finishedWithData.reduce((acc, t) => acc + (t.correctAnswers / t.totalQuestions), 0) / finishedWithData.length
      : 0
    return {
      finalized,
      notFinalized,
      total: lastTestes.length,
      avgAccuracy: Math.round(avgAccuracy * 100) + '%',
    }
  }, [lastTestes])
  return (
    <Box p={3}>
      <SectionHeader
        icon={<Assignment className={classes.sectionIcon} />}
        title="Últimas Avaliações Realizadas"
        subtitle="Acompanhe seu desempenho nas avaliações realizadas."
      />

      <Grid container spacing={2} style={{marginBottom: 24}}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CheckCircleOutline style={{fontSize: 48, color: '#4caf50'}} />}
            value={stats.finalized}
            label="Finalizadas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Schedule style={{fontSize: 48, color: '#ff9800'}} />}
            value={stats.notFinalized}
            label="Não finalizadas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrackChanges style={{fontSize: 48, color: '#29b6f6'}} />}
            value={stats.avgAccuracy}
            label="Média de acertos"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Description style={{fontSize: 48, color: '#ce93d8'}} />}
            value={stats.total}
            label="Avaliações realizadas"
          />
        </Grid>
      </Grid>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Avaliação</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Questões</TableCell>
              <TableCell>Acertos</TableCell>
              <TableCell>% de acertos</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lastTestes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((test) => (
              <TableRow key={test.id}>
                <TableCell>{test.availabilityName}</TableCell>
                <TableCell>{assessmentTypeLabels[test.availabilityAssessmentType] ?? '-'}</TableCell>
                <TableCell>
                  {test.availabilityAssessmentType === 'printed'
                    ? <StatusPrintedChip status={test.status} />
                    : <StatusChip status={test.status} />}
                </TableCell>
                <TableCell>{formatDateTime(test.startedAt)}</TableCell>
                <TableCell>{test.totalQuestions}</TableCell>
                <TableCell>{test.status === 'finished' ? test.correctAnswers : '-'}</TableCell>
                <TableCell>
                  {test.status === 'finished'
                    ? <QuizAssessmentTestGradeComponent {...test} />
                    : '-'
                  }
                </TableCell>
                <TableCell>
                  <Box
                    className={test.status === 'finished'
                      ? classes.viewDetails
                      : `${classes.viewDetails} ${classes.viewDetailsDisabled}`}
                    onClick={test.status === 'finished' ? () => setSelectedTest(test) : undefined}
                    aria-disabled={test.status !== 'finished'}
                  >
                    <Visibility style={{fontSize: 18}} />
                    <span>Ver detalhes</span>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {lastTestes.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhuma avaliação realizada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={lastTestes.length}
          page={page}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      <AssessmentDetailsDialog test={selectedTest} onClose={() => setSelectedTest(null)} />
    </Box>
  )
}


function AssessmentDetailsDialog({ test, onClose }) {
  return (
    <Dialog open={!!test} onClose={onClose} maxWidth="lg" fullWidth>
      {/*<DialogTitle>Detalhes da Avaliação</DialogTitle>*/}
      <DialogContent dividers>
        {test && (
          <Box display="flex" flexDirection="column" style={{gap: 16, padding: 0}}>
            {test?.questions?.length > 0 && (
              <>
                <Divider />
                <AssessmentQuestionWizard
                  questions={test.questions}
                  readOnly
                  response={test.response}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  )
}